import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const EditRolesModal = ({ roleType, onClose }) => {
  const [rolesList, setRolesList] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [newRole, setNewRole] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch roles from database on mount
  useEffect(() => {
    fetchRolesFromDatabase();
  }, [roleType]);

  const fetchRolesFromDatabase = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('personnel_custom_roles')
        ?.select('*')
        ?.eq('role_type', roleType)
        ?.order('role_name', { ascending: true });

      if (error) {
        console.error('Error fetching roles:', error?.message);
        setError('Failed to load roles from database');
        setRolesList([]);
      } else {
        // Extract just the role names as strings
        const roleNames = data?.map(role => role?.role_name) || [];
        setRolesList(roleNames);
      }
    } catch (err) {
      console.error('Failed to fetch roles:', err);
      setError('An error occurred while loading roles');
      setRolesList([]);
    } finally {
      setLoading(false);
    }
  };

  const getRoleTypeLabel = () => {
    switch (roleType) {
      case 'technicians':
        return 'Technician Roles';
      case 'managers':
        return 'Manager Roles';
      case 'sales':
        return 'Sales Roles';
      default:
        return 'Roles';
    }
  };

  const handleAddRole = () => {
    if (!newRole?.trim()) {
      setError('Role name cannot be empty');
      return;
    }

    if (rolesList?.some(r => r?.toLowerCase() === newRole?.trim()?.toLowerCase())) {
      setError('This role already exists');
      return;
    }

    setRolesList(prev => [...prev, newRole?.trim()]);
    setNewRole('');
    setError('');
  };

  const handleStartEdit = (index) => {
    setEditingIndex(index);
    setEditValue(rolesList?.[index]);
    setError('');
  };

  const handleSaveEdit = () => {
    if (!editValue?.trim()) {
      setError('Role name cannot be empty');
      return;
    }

    if (rolesList?.some((r, idx) => idx !== editingIndex && r?.toLowerCase() === editValue?.trim()?.toLowerCase())) {
      setError('This role already exists');
      return;
    }

    const updated = [...rolesList];
    updated[editingIndex] = editValue?.trim();
    setRolesList(updated);
    setEditingIndex(null);
    setEditValue('');
    setError('');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditValue('');
    setError('');
  };

  const handleDeleteRole = (index) => {
    setRolesList(prev => prev?.filter((_, idx) => idx !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
      setEditValue('');
    }
    setError('');
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      setError('');

      // Delete all existing roles for this role type
      const { error: deleteError } = await supabase
        ?.from('personnel_custom_roles')
        ?.delete()
        ?.eq('role_type', roleType);

      if (deleteError) {
        console.error('Error deleting old roles:', deleteError?.message);
        setError(`Failed to update roles: ${deleteError?.message}`);
        return;
      }

      // Insert new roles if any exist
      if (rolesList?.length > 0) {
        const rolesToInsert = rolesList?.map(roleName => ({
          role_type: roleType,
          role_name: roleName
        }));

        const { error: insertError } = await supabase
          ?.from('personnel_custom_roles')
          ?.insert(rolesToInsert);

        if (insertError) {
          console.error('Error inserting new roles:', insertError?.message);
          setError(`Failed to save roles: ${insertError?.message}`);
          return;
        }
      }

      // Success - close modal and trigger parent refresh
      onClose(true); // Pass true to indicate successful save
    } catch (err) {
      console.error('Failed to save roles:', err);
      setError('An error occurred while saving roles');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1400] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between z-10">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
            Edit {getRoleTypeLabel()}
          </h2>
          <button
            onClick={() => onClose(false)}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
            disabled={saving}
          >
            <Icon name="X" size={24} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading roles...</p>
            </div>
          ) : (
            <>
              {/* Add New Role Section */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                  Add New Role
                </h3>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={newRole}
                    onChange={(e) => {
                      setNewRole(e?.target?.value);
                      setError('');
                    }}
                    placeholder="Enter role name"
                    onKeyPress={(e) => {
                      if (e?.key === 'Enter') {
                        handleAddRole();
                      }
                    }}
                    disabled={saving}
                  />
                  <Button
                    variant="default"
                    onClick={handleAddRole}
                    className="whitespace-nowrap"
                    disabled={saving}
                  >
                    <Icon name="Plus" size={18} color="white" />
                    Add
                  </Button>
                </div>
                {error && (
                  <p className="text-sm text-destructive">{error}</p>
                )}
              </div>

              {/* Existing Roles List */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                  Current Roles ({rolesList?.length})
                </h3>
                {rolesList?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Icon name="Briefcase" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
                    <p>No roles defined yet. Add your first role above.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {rolesList?.map((role, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 p-3 bg-background border border-border rounded-lg hover:border-primary/50 transition-smooth"
                      >
                        {editingIndex === index ? (
                          <>
                            <Input
                              type="text"
                              value={editValue}
                              onChange={(e) => {
                                setEditValue(e?.target?.value);
                                setError('');
                              }}
                              onKeyPress={(e) => {
                                if (e?.key === 'Enter') {
                                  handleSaveEdit();
                                } else if (e?.key === 'Escape') {
                                  handleCancelEdit();
                                }
                              }}
                              autoFocus
                              disabled={saving}
                            />
                            <button
                              onClick={handleSaveEdit}
                              className="p-2 rounded-lg bg-primary hover:bg-primary/90 transition-smooth"
                              title="Save"
                              disabled={saving}
                            >
                              <Icon name="Check" size={18} color="white" />
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="p-2 rounded-lg bg-muted hover:bg-muted/80 transition-smooth"
                              title="Cancel"
                              disabled={saving}
                            >
                              <Icon name="X" size={18} color="var(--color-foreground)" />
                            </button>
                          </>
                        ) : (
                          <>
                            <div className="flex-1 flex items-center gap-2">
                              <Icon name="Briefcase" size={18} color="var(--color-primary)" />
                              <span className="text-foreground font-medium">{role}</span>
                            </div>
                            <button
                              onClick={() => handleStartEdit(index)}
                              className="p-2 rounded-lg hover:bg-muted transition-smooth"
                              title="Edit"
                              disabled={saving}
                            >
                              <Icon name="Edit" size={18} color="var(--color-foreground)" />
                            </button>
                            <button
                              onClick={() => handleDeleteRole(index)}
                              className="p-2 rounded-lg hover:bg-destructive/10 transition-smooth"
                              title="Delete"
                              disabled={saving}
                            >
                              <Icon name="Trash2" size={18} color="var(--color-destructive)" />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-6 border-t border-border">
                <Button 
                  variant="default" 
                  onClick={handleSaveAll} 
                  fullWidth
                  disabled={saving}
                  loading={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => onClose(false)} 
                  fullWidth
                  disabled={saving}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditRolesModal;